import React, { useEffect, useState, useRef } from 'react';
import { NavBar } from '../components/NavBar';
import { StatCard, TrendIndicator } from '../components/StatCard';
import { Button } from '../components/SubscriptionAppButton';
import { Send, Sparkles, MoreVertical, Download, Trash2, MessageSquare, User, HelpCircle, Bell } from 'lucide-react';
// Types for chat messages
interface Message {
  id: string;
  role: 'user' | 'ai';
  content: string;
  timestamp: string;
}
// Mock initial messages
const initialMessages: Message[] = [{
  id: '1',
  role: 'user',
  content: 'What was our churn rate last month?',
  timestamp: '3 minutes ago'
}, {
  id: '2',
  role: 'ai',
  content: "Your churn rate last month was 2.1%, which is a 0.5% improvement compared to the previous month. The main reasons for cancellation were 'Too expensive' (34%) and 'Not using enough' (28%). Would you like me to break this down by subscription tier?",
  timestamp: '2 minutes ago'
}, {
  id: '3',
  role: 'user',
  content: 'Yes, show me by tier',
  timestamp: '1 minute ago'
}, {
  id: '4',
  role: 'ai',
  content: "Here's the churn breakdown by tier:\n\n• Premium Wine Club: 1.8% (lowest)\n• Standard Box: 2.4%\n• Starter Pack: 3.1% (highest)\n\nThe Starter Pack tier has the highest churn, likely due to lower commitment. Consider offering an upgrade incentive after the 3rd order.",
  timestamp: 'Just now'
}];
// Suggested questions
const suggestedQuestions = ['Who are my at-risk customers?', "What's my revenue forecast?", 'Show me retention trends', 'Which products are most popular?', 'Compare this month to last month'];
export function AIChatPage() {
  const [messages, setMessages] = useState<Message[]>(initialMessages);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // Auto-scroll to bottom of chat
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  const handleSendMessage = () => {
    if (!inputValue.trim()) return;
    // Add user message
    const userMsg: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: inputValue,
      timestamp: 'Just now'
    };
    setMessages(prev => [...prev, userMsg]);
    setInputValue('');
    setIsTyping(true);
    // Simulate AI response
    setTimeout(() => {
      const aiMsg: Message = {
        id: (Date.now() + 1).toString(),
        role: 'ai',
        content: "I've analyzed your data based on that request. Here's what I found...",
        timestamp: 'Just now'
      };
      setMessages(prev => [...prev, aiMsg]);
      setIsTyping(false);
    }, 1500);
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  return <div className="bg-[#F1F5F9] min-h-full p-6 flex flex-col h-screen overflow-hidden" data-id="element-6394" data-mp="1f2m3027n1nkw">
      {/* NavBar */}
      <div className="mb-6 flex-shrink-0" data-id="element-6395" data-mp="2zm7rw0652vgc">
        <NavBar tabs={[]} title="AI Chat" data-id="element-6396" data-mp="2vkmkuvgadbe2" />
      </div>

      <div className="flex flex-col flex-1 overflow-hidden max-w-[1280px] mx-auto w-full" data-id="element-6397" data-mp="2vm7fel2loubx">
        {/* Header with Subtitle and Actions - Custom implementation to match requirement */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 flex-shrink-0" data-id="element-6398" data-mp="34k9cwqn4l4ug">
          <div data-id="element-6399" data-mp="3j843o1hkoz4w">
            <h1 className="text-[28px] font-semibold text-[#0F172A]" data-id="element-6400" data-mp="3jc1n6o82bspb">
              AI Chat
            </h1>
            <p className="text-[14px] text-[#64748B]" data-id="element-6401" data-mp="3ed70hb6z5jzx">
              Ask questions about your subscriptions, customers, and analytics
            </p>
          </div>
          <div className="flex items-center gap-3" data-id="element-6402" data-mp="3nc2209z2q5ww">
            <button className="p-2 text-[#64748B] hover:text-[#0F172A] hover:bg-white rounded-full transition-colors" data-id="element-6403" data-mp="1xwciuggi2nmy">
              <Bell className="w-5 h-5" data-id="element-6404" data-mp="1syatxewe7u3k" />
            </button>
            <button className="p-2 text-[#64748B] hover:text-[#0F172A] hover:bg-white rounded-full transition-colors" data-id="element-6405" data-mp="1vc6dc4c8mp4a">
              <HelpCircle className="w-5 h-5" data-id="element-6406" data-mp="1zd8cyma2h36s" />
            </button>
            <Button variant="primary" iconLeft={<Sparkles className="w-4 h-4" data-id="element-6408" data-mp="24ccj141p2ksw" />} data-id="element-6407" data-mp="1zfm6gqp3m7c8">
              Ask WinePik
            </Button>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6 flex-shrink-0" data-id="element-6409" data-mp="29b5y30wmqjv6">
          <StatCard label="QUESTIONS TODAY" value="12" trend={<TrendIndicator value={3} label="vs yesterday" direction="up" data-id="element-6411" data-mp="1gmiagyoerieg" />} data-id="element-6410" data-mp="1bq063zwzpm2n" />
          <StatCard label="INSIGHTS GENERATED" value="847" secondaryValue="All time" data-id="element-6412" data-mp="1lmcymugsttvt" />
          <StatCard label="POPULAR TOPICS" value="Retention" secondaryValue="Revenue, Churn" data-id="element-6413" data-mp="1lkjrle29x7na" />
          <StatCard label="LAST ACTIVE" value="2m ago" secondaryValue="Sarah asked about Q4 revenue" data-id="element-6414" data-mp="1qewfq1ep375d" />
        </div>

        {/* Chat Container */}
        <div className="bg-white rounded-lg shadow-sm border border-[#E2E8F0] flex flex-col flex-1 overflow-hidden min-h-[400px]" data-id="element-6415" data-mp="xvqtpc23cpq1">
          {/* Chat Header Bar */}
          <div className="h-[56px] border-b border-[#E2E8F0] px-6 flex items-center justify-between flex-shrink-0 bg-white rounded-t-lg" data-id="element-6416" data-mp="le6j1whcvoud">
            <div className="flex items-center gap-4" data-id="element-6417" data-mp="ge1ztyp2cg0g">
              <span className="text-[14px] font-semibold text-[#0F172A]" data-id="element-6418" data-mp="bg0jvmmxowt7">
                Conversation
              </span>
              <Button variant="secondary" size="sm" iconLeft={<MessageSquare className="w-3.5 h-3.5" data-id="element-6420" data-mp="7fq6vq0q564u" />} data-id="element-6419" data-mp="7ene1cjuctma">
                New Chat
              </Button>
            </div>
            <div className="flex items-center gap-3" data-id="element-6421" data-mp="3ttzqzmxmfrzt">
              <button className="text-[13px] font-medium text-[#64748B] hover:text-[#EF4444] transition-colors flex items-center gap-1.5" data-id="element-6422" data-mp="3turjupzu3bme">
                <Trash2 className="w-3.5 h-3.5" data-id="element-6423" data-mp="3ot2cbkbtlg32" />
                Clear History
              </button>
              <div className="h-4 w-px bg-[#E2E8F0]" data-id="element-6424" data-mp="3fu72ly9khnv0" />
              <Button variant="secondary" size="sm" iconLeft={<Download className="w-3.5 h-3.5" data-id="element-6426" data-mp="3593eteg45ix8" />} data-id="element-6425" data-mp="3fvi9w8281dqv">
                Export Chat
              </Button>
            </div>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-6 bg-[#F8FAFC] space-y-6" data-id="element-6427" data-mp="3a9hot219z2xw">
            {messages.map(msg => <div key={msg.id} className={`flex gap-4 ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`} data-id="element-6428" data-mp="2wa9kjuq994mu">
                {/* Avatar */}
                <div className={`
                  w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0
                  ${msg.role === 'ai' ? 'bg-[#0EA5E9] text-white' : 'bg-[#64748B] text-white'}
                `} data-id="element-6429" data-mp="3owkmp9tifdlr">
                  {msg.role === 'ai' ? <Sparkles className="w-4 h-4" data-id="element-6430" data-mp="3f56s9ggf13zh" /> : <span className="text-xs font-medium" data-id="element-6431" data-mp="3k644lceqv3t2">SC</span>}
                </div>

                {/* Message Bubble */}
                <div className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'} max-w-[80%]`} data-id="element-6432" data-mp="bpj46k06hj1k">
                  <div className={`
                    px-4 py-3 rounded-lg shadow-sm text-[14px] leading-relaxed whitespace-pre-wrap
                    ${msg.role === 'ai' ? 'bg-white text-[#0F172A] border border-[#E2E8F0] rounded-tl-none' : 'bg-[#0EA5E9] text-white rounded-tr-none'}
                  `} data-id="element-6433" data-mp="giu4e2ahj1ki">
                    {msg.content}
                  </div>
                  <span className="text-[12px] text-[#94A3B8] mt-1.5 px-1" data-id="element-6434" data-mp="umjavrnpk0fn">
                    {msg.timestamp}
                  </span>
                </div>
              </div>)}

            {isTyping && <div className="flex gap-4" data-id="element-6435" data-mp="2w5229ujxeau">
                <div className="w-8 h-8 rounded-full bg-[#0EA5E9] text-white flex items-center justify-center flex-shrink-0" data-id="element-6436" data-mp="2x7wusuzw0w1">
                  <Sparkles className="w-4 h-4" data-id="element-6437" data-mp="gt1uugu8d5ud" />
                </div>
                <div className="bg-white border border-[#E2E8F0] px-4 py-3 rounded-lg rounded-tl-none shadow-sm" data-id="element-6438" data-mp="buqls8brezd7">
                  <div className="flex gap-1" data-id="element-6439" data-mp="1sco05rm8mse0">
                    <div className="w-2 h-2 bg-[#94A3B8] rounded-full animate-bounce" style={{
                  animationDelay: '0ms'
                }} data-id="element-6440" data-mp="1nc9qk54znadv" />
                    <div className="w-2 h-2 bg-[#94A3B8] rounded-full animate-bounce" style={{
                  animationDelay: '150ms'
                }} data-id="element-6441" data-mp="3hpvqellgchpy" />
                    <div className="w-2 h-2 bg-[#94A3B8] rounded-full animate-bounce" style={{
                  animationDelay: '300ms'
                }} data-id="element-6442" data-mp="3qrlhfqbf9en7" />
                  </div>
                </div>
              </div>}
            <div ref={messagesEndRef} data-id="element-6443" data-mp="4ciwf928yxi0" />
          </div>

          {/* Input Area */}
          <div className="bg-white border-t border-[#E2E8F0] p-6 flex-shrink-0" data-id="element-6444" data-mp="9akd5nc7akqb">
            {/* Suggested Questions */}
            <div className="mb-4" data-id="element-6445" data-mp="dbe30m97e9lu">
              <div className="flex items-center gap-2 mb-2" data-id="element-6446" data-mp="icbj8tanktkp">
                <span className="text-[12px] font-medium text-[#64748B]" data-id="element-6447" data-mp="iah9mb4u0nvx">
                  Try asking:
                </span>
              </div>
              <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide" data-id="element-6448" data-mp="2k3evto43si7w">
                {suggestedQuestions.map((question, index) => <button key={index} onClick={() => {
                setInputValue(question);
                // Optional: auto-send
              }} className="whitespace-nowrap px-3 py-1.5 rounded-full border border-[#E2E8F0] text-[13px] text-[#475569] hover:border-[#0EA5E9] hover:text-[#0EA5E9] hover:bg-[#F0F9FF] transition-colors bg-white" data-id="element-6449" data-mp="2cl7b525jd4j1" data-mpi={index}>
                    {question}
                  </button>)}
              </div>
            </div>

            {/* Input Field */}
            <div className="flex gap-3" data-id="element-6450" data-mp="2qjx72egmcy5t">
              <input type="text" value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyDown={handleKeyDown} placeholder="Ask anything about your wine club..." className="flex-1 h-[48px] px-4 rounded-lg border border-[#E2E8F0] text-[14px] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 transition-all" data-id="element-6451" data-mp="2qkzvmfqgyoik" />
              <button onClick={handleSendMessage} disabled={!inputValue.trim() || isTyping} className="h-[48px] w-[48px] flex items-center justify-center rounded-lg bg-[#0EA5E9] text-white hover:bg-[#0284C7] disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm" data-id="element-6452" data-mp="162savioae32d">
                <Send className="w-5 h-5" data-id="element-6453" data-mp="3kdpq1ych3ky3" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>;
}